import type {
	AiEvaluationCase,
	AiEvaluationCaseInput,
	AiEvaluationComparison,
	AiEvaluationExecution,
	DepartmentSummary,
	TenantAiSettings,
	TenantMemberSummary,
} from '@livechat/shared';
import { type FormEvent, useEffect, useState } from 'react';

import {
	createTenantAiEvaluationCase,
	deleteTenantAiEvaluationCase,
	listDepartments,
	listMembers,
	listTenantAiEvaluationCases,
	resetTenantAiDraftToPublished,
	runTenantAiEvaluationCase,
	updateTenantAiEvaluationCase,
} from '../api.js';
import { useDateFormat } from '../date-format.js';

interface EvaluationForm {
	agentUserId: string;
	departmentId: string;
	expectedGuardrailCode: string;
	expectedOutcome: AiEvaluationCaseInput['expectedOutcome'];
	forbiddenResponsePhrases: string;
	minimumRelevanceScore: string;
	name: string;
	priorGuardrailClarifications: number;
	requiredResponsePhrases: string;
	visitorMessage: string;
}

function emptyForm(agentUserId = ''): EvaluationForm {
	return {
		agentUserId,
		departmentId: '',
		expectedGuardrailCode: '',
		expectedOutcome: 'answer',
		forbiddenResponsePhrases: '',
		minimumRelevanceScore: '',
		name: '',
		priorGuardrailClarifications: 0,
		requiredResponsePhrases: '',
		visitorMessage: '',
	};
}

function TrashIcon() {
	return (
		<svg aria-hidden="true" fill="none" viewBox="0 0 24 24">
			<path d="M4 7h16M9 7V4h6v3m3 0-1 13H7L6 7m4 4v5m4-5v5" />
		</svg>
	);
}

function phraseList(value: string): string[] {
	const seen = new Set<string>();
	return value
		.split(/\r?\n/)
		.map((phrase) => phrase.trim())
		.filter((phrase) => {
			const key = phrase.toLocaleLowerCase();
			if (!key || seen.has(key)) return false;
			seen.add(key);
			return true;
		});
}

function comparisonLabel(comparison: AiEvaluationComparison['comparison']): string {
	switch (comparison) {
		case 'both_pass':
			return 'Both pass';
		case 'improved':
			return 'Draft improves';
		case 'regressed':
			return 'Draft regresses';
		case 'both_fail':
			return 'Both fail';
	}
}

function outcomeLabel(value: AiEvaluationCaseInput['expectedOutcome']): string {
	switch (value) {
		case 'answer':
			return 'Answer';
		case 'clarify':
			return 'Ask for detail';
		case 'handoff':
			return 'Hand off';
	}
}

function resultLabel(execution?: AiEvaluationExecution): string {
	if (!execution) return 'Not run';
	if (execution.status === 'failed') return 'Could not run';
	return execution.passed ? 'Pass' : 'Fail';
}

function EvaluationEvidence({ execution }: { execution: AiEvaluationExecution }) {
	return (
		<div className={`ai-evaluation-result ${execution.passed ? 'pass' : 'fail'}`}>
			<header>
				<div>
					<strong>{execution.target === 'draft' ? 'Draft' : 'Published'}</strong>
					{execution.tenantVersion && (
						<span>
							Tenant v{execution.tenantVersion} · Platform v
							{execution.platformVersion}
						</span>
					)}
				</div>
				<span
					className={`ui-badge ${execution.passed ? 'ui-badge-success' : 'ui-badge-danger'}`}
				>
					{resultLabel(execution)}
				</span>
			</header>
			{execution.errorMessage && <p>{execution.errorMessage}</p>}
			{execution.result && (
				<>
					<div className="ai-evaluation-response">
						<span>Response evidence</span>
						<p>{execution.result.message}</p>
						<small>
							{execution.result.model} · {execution.result.reasoningEffort} ·{' '}
							{execution.result.durationMs} ms
							{execution.result.topRelevanceScore === undefined
								? ''
								: ` · top evidence ${execution.result.topRelevanceScore.toFixed(2)}`}
						</small>
					</div>
					{!!execution.result.sources.length && (
						<ul className="ai-evaluation-sources">
							{execution.result.sources.map((source) => (
								<li key={`${source.sourceId}-${source.rank}`}>
									{source.url ? (
										<a href={source.url} rel="noreferrer" target="_blank">
											{source.title}
										</a>
									) : (
										source.title
									)}
									<span>
										{source.relevanceScore.toFixed(2)}
										{source.used ? ' · cited' : ''}
									</span>
								</li>
							))}
						</ul>
					)}
				</>
			)}
			<ul className="ai-evaluation-checks">
				{execution.checks.map((check, index) => (
					<li className={check.passed ? 'pass' : 'fail'} key={`${check.kind}-${index}`}>
						<span>{check.passed ? 'Pass' : 'Fail'}</span>
						<div>
							<strong>{check.kind.replaceAll('_', ' ')}</strong>
							<small>
								Expected {check.expected}; actual {check.actual}
							</small>
						</div>
					</li>
				))}
			</ul>
		</div>
	);
}

export function TenantAiEvaluation({
	disabled = false,
	onDraftReset,
	tenantId,
}: {
	disabled?: boolean;
	onDraftReset: (settings: TenantAiSettings) => void;
	tenantId: string;
}) {
	const { formatDateTime } = useDateFormat();
	const [cases, setCases] = useState<AiEvaluationCase[]>([]);
	const [agents, setAgents] = useState<TenantMemberSummary[]>([]);
	const [departments, setDepartments] = useState<DepartmentSummary[]>([]);
	const [form, setForm] = useState<EvaluationForm>(() => emptyForm());
	const [editingId, setEditingId] = useState<string>();
	const [showForm, setShowForm] = useState(false);
	const [loading, setLoading] = useState(true);
	const [busy, setBusy] = useState(false);
	const [runningId, setRunningId] = useState<string>();
	const [runProgress, setRunProgress] = useState<string>();
	const [error, setError] = useState<string>();
	const [message, setMessage] = useState<string>();

	useEffect(() => {
		let current = true;
		setLoading(true);
		setError(undefined);
		void Promise.all([
			listTenantAiEvaluationCases(tenantId),
			listMembers(tenantId),
			listDepartments(tenantId),
		])
			.then(([loadedCases, members, loadedDepartments]) => {
				if (!current) return;
				const activeAgents = members.filter(
					(member) => member.memberType === 'ai_agent' && member.status === 'active',
				);
				setCases(loadedCases);
				setAgents(activeAgents);
				setDepartments(
					loadedDepartments.filter((department) => department.status === 'active'),
				);
				setForm((value) => ({
					...value,
					agentUserId: value.agentUserId || activeAgents[0]?.id || '',
				}));
			})
			.catch((caught: unknown) => {
				if (current) {
					setError(
						caught instanceof Error
							? caught.message
							: 'Could not load regression testing',
					);
				}
			})
			.finally(() => {
				if (current) setLoading(false);
			});
		return () => {
			current = false;
		};
	}, [tenantId]);

	function closeForm(): void {
		setEditingId(undefined);
		setForm(emptyForm(agents[0]?.id));
		setShowForm(false);
	}

	function updateFormField<Key extends keyof EvaluationForm>(
		key: Key,
		value: EvaluationForm[Key],
	): void {
		setForm((current) => ({ ...current, [key]: value }));
	}

	function edit(evaluationCase: AiEvaluationCase): void {
		setEditingId(evaluationCase.id);
		setForm({
			agentUserId: evaluationCase.agentUserId,
			departmentId: evaluationCase.departmentId ?? '',
			expectedGuardrailCode: evaluationCase.expectedGuardrailCode ?? '',
			expectedOutcome: evaluationCase.expectedOutcome,
			forbiddenResponsePhrases: evaluationCase.forbiddenResponsePhrases.join('\n'),
			minimumRelevanceScore: evaluationCase.minimumRelevanceScore?.toString() ?? '',
			name: evaluationCase.name,
			priorGuardrailClarifications: evaluationCase.priorGuardrailClarifications,
			requiredResponsePhrases: evaluationCase.requiredResponsePhrases.join('\n'),
			visitorMessage: evaluationCase.visitorMessage,
		});
		setShowForm(true);
		setError(undefined);
		setMessage(undefined);
	}

	async function save(event: FormEvent<HTMLFormElement>): Promise<void> {
		event.preventDefault();
		if (busy || !form.agentUserId) return;
		setBusy(true);
		setError(undefined);
		setMessage(undefined);
		const input: AiEvaluationCaseInput = {
			agentUserId: form.agentUserId,
			...(form.departmentId ? { departmentId: form.departmentId } : {}),
			...(form.expectedGuardrailCode.trim()
				? {
						expectedGuardrailCode: form.expectedGuardrailCode
							.trim()
							.toLocaleLowerCase(),
					}
				: {}),
			expectedOutcome: form.expectedOutcome,
			forbiddenResponsePhrases: phraseList(form.forbiddenResponsePhrases),
			...(form.minimumRelevanceScore === ''
				? {}
				: { minimumRelevanceScore: Number(form.minimumRelevanceScore) }),
			name: form.name.trim(),
			priorGuardrailClarifications: form.priorGuardrailClarifications,
			requiredResponsePhrases: phraseList(form.requiredResponsePhrases),
			visitorMessage: form.visitorMessage.trim(),
		};
		try {
			const saved = editingId
				? await updateTenantAiEvaluationCase(tenantId, editingId, input)
				: await createTenantAiEvaluationCase(tenantId, input);
			setCases((current) =>
				editingId
					? current.map((item) => (item.id === saved.id ? saved : item))
					: [saved, ...current],
			);
			setMessage(editingId ? 'Regression case updated.' : 'Regression case created.');
			closeForm();
		} catch (caught) {
			setError(
				caught instanceof Error ? caught.message : 'Could not save the regression case',
			);
		} finally {
			setBusy(false);
		}
	}

	async function run(
		evaluationCase: AiEvaluationCase,
	): Promise<AiEvaluationComparison | undefined> {
		if (busy || runningId || disabled) return undefined;
		setRunningId(evaluationCase.id);
		setError(undefined);
		setMessage(undefined);
		try {
			const comparison = await runTenantAiEvaluationCase(tenantId, evaluationCase.id);
			setCases((current) =>
				current.map((item) =>
					item.id === evaluationCase.id
						? { ...item, latestComparison: comparison }
						: item,
				),
			);
			return comparison;
		} catch (caught) {
			setError(
				caught instanceof Error ? caught.message : 'Could not run the regression case',
			);
			return undefined;
		} finally {
			setRunningId(undefined);
		}
	}

	async function runAll(): Promise<void> {
		if (
			busy ||
			runningId ||
			disabled ||
			!cases.length ||
			!window.confirm(
				`Run all ${cases.length} regression ${cases.length === 1 ? 'case' : 'cases'}? Each case calls the AI provider for Draft and Published.`,
			)
		) {
			return;
		}
		let completed = 0;
		for (const evaluationCase of cases) {
			setRunProgress(`${completed + 1} of ${cases.length}`);
			await run(evaluationCase);
			completed += 1;
		}
		setRunProgress(undefined);
		setMessage(`Completed ${completed} regression ${completed === 1 ? 'case' : 'cases'}.`);
	}

	async function remove(evaluationCase: AiEvaluationCase): Promise<void> {
		if (
			busy ||
			runningId ||
			!window.confirm(`Delete the regression case “${evaluationCase.name}”?`)
		) {
			return;
		}
		setBusy(true);
		setError(undefined);
		try {
			await deleteTenantAiEvaluationCase(tenantId, evaluationCase.id);
			setCases((current) => current.filter((item) => item.id !== evaluationCase.id));
			setMessage('Regression case deleted.');
		} catch (caught) {
			setError(
				caught instanceof Error ? caught.message : 'Could not delete the regression case',
			);
		} finally {
			setBusy(false);
		}
	}

	async function resetDraft(): Promise<void> {
		if (
			busy ||
			runningId ||
			disabled ||
			!window.confirm(
				'Reset the saved Draft to the current Published configuration? This discards Draft changes but does not change the live Published version.',
			)
		) {
			return;
		}
		setBusy(true);
		setError(undefined);
		setMessage(undefined);
		try {
			const settings = await resetTenantAiDraftToPublished(tenantId);
			onDraftReset(settings);
			setMessage('Draft reset to the current Published configuration.');
		} catch (caught) {
			setError(caught instanceof Error ? caught.message : 'Could not reset the Draft');
		} finally {
			setBusy(false);
		}
	}

	const hasRegression = cases.some(
		(evaluationCase) => evaluationCase.latestComparison?.rollbackRecommended,
	);

	return (
		<section className="settings-card integration-panel ai-evaluation-panel">
			<div className="integration-panel-heading ai-evaluation-heading">
				<div>
					<h4 className="panel-title">Regression testing</h4>
					<p>
						Keep reusable expected outcomes and compare the same test against the saved
						Draft and current Published configurations. Results contain safe evidence,
						not hidden reasoning.
					</p>
				</div>
				<div className="ai-evaluation-heading-actions">
					<button
						className="secondary-button"
						disabled={busy || Boolean(runningId) || disabled || !cases.length}
						onClick={() => void runAll()}
						type="button"
					>
						{runProgress ? `Running ${runProgress}…` : 'Run all'}
					</button>
					<button
						className="primary-button"
						disabled={
							busy || Boolean(runningId) || agents.length === 0 || cases.length >= 20
						}
						onClick={() => {
							setEditingId(undefined);
							setForm(emptyForm(agents[0]?.id));
							setShowForm(true);
						}}
						type="button"
					>
						Add regression case
					</button>
				</div>
			</div>

			{error && <div className="alert error">{error}</div>}
			{message && <div className="alert success">{message}</div>}
			{disabled && (
				<div className="ai-simulator-warning">
					Save the AI settings above before comparing those Draft changes.
				</div>
			)}
			{hasRegression && (
				<div className="ai-evaluation-rollback">
					<div>
						<strong>A saved Draft regression was detected</strong>
						<span>
							You can reset the Draft safely. Published chats and active pinned
							conversations will not change.
						</span>
					</div>
					<button
						className="secondary-button"
						disabled={busy || Boolean(runningId) || disabled}
						onClick={() => void resetDraft()}
						type="button"
					>
						Reset Draft to Published
					</button>
				</div>
			)}

			{showForm && (
				<form className="ai-evaluation-form" onSubmit={(event) => void save(event)}>
					<div className="integration-panel-heading">
						<h4 className="panel-title">
							{editingId ? 'Edit regression case' : 'New regression case'}
						</h4>
						<p>
							Each case is a single visitor turn. Use the Draft simulator above for
							interactive multi-turn exploration.
						</p>
					</div>
					<div className="ai-evaluation-form-grid">
						<label>
							<span>Case name</span>
							<input
								maxLength={160}
								onChange={(event) =>
									updateFormField('name', event.currentTarget.value)
								}
								required
								value={form.name}
							/>
						</label>
						<label>
							<span>AI agent</span>
							<select
								onChange={(event) =>
									updateFormField('agentUserId', event.currentTarget.value)
								}
								required
								value={form.agentUserId}
							>
								{agents.map((agent) => (
									<option key={agent.id} value={agent.id}>
										{agent.displayName}
									</option>
								))}
							</select>
						</label>
						<label>
							<span>Department context</span>
							<select
								onChange={(event) =>
									updateFormField('departmentId', event.currentTarget.value)
								}
								value={form.departmentId}
							>
								<option value="">No department</option>
								{departments.map((department) => (
									<option key={department.id} value={department.id}>
										{department.name}
									</option>
								))}
							</select>
						</label>
						<label>
							<span>Expected decision</span>
							<select
								onChange={(event) =>
									updateFormField(
										'expectedOutcome',
										event.currentTarget
											.value as EvaluationForm['expectedOutcome'],
									)
								}
								value={form.expectedOutcome}
							>
								<option value="answer">Answer</option>
								<option value="clarify">Ask for more detail</option>
								<option value="handoff">Hand off to a person</option>
							</select>
						</label>
						<label>
							<span>Previous guardrail clarifications</span>
							<select
								onChange={(event) =>
									updateFormField(
										'priorGuardrailClarifications',
										Number(event.currentTarget.value),
									)
								}
								value={form.priorGuardrailClarifications}
							>
								<option value="0">None</option>
								<option value="1">One</option>
								<option value="2">Two</option>
								<option value="3">Three</option>
							</select>
						</label>
						<label>
							<span>Expected guardrail code (optional)</span>
							<input
								maxLength={80}
								onChange={(event) =>
									updateFormField(
										'expectedGuardrailCode',
										event.currentTarget.value,
									)
								}
								pattern="[a-zA-Z0-9_]+"
								placeholder="e.g. low_confidence"
								value={form.expectedGuardrailCode}
							/>
						</label>
						<label>
							<span>Minimum evidence score (optional)</span>
							<input
								max="1.5"
								min="0"
								onChange={(event) =>
									updateFormField(
										'minimumRelevanceScore',
										event.currentTarget.value,
									)
								}
								step="0.05"
								type="number"
								value={form.minimumRelevanceScore}
							/>
						</label>
					</div>
					<label className="ai-evaluation-message-field">
						<span>Visitor message</span>
						<textarea
							maxLength={4_000}
							onChange={(event) =>
								updateFormField('visitorMessage', event.currentTarget.value)
							}
							required
							rows={3}
							value={form.visitorMessage}
						/>
					</label>
					<div className="ai-evaluation-phrase-grid">
						<label>
							<span>Required response phrases</span>
							<textarea
								maxLength={1_010}
								onChange={(event) =>
									updateFormField(
										'requiredResponsePhrases',
										event.currentTarget.value,
									)
								}
								placeholder="One phrase per line"
								rows={3}
								value={form.requiredResponsePhrases}
							/>
							<small>Case-insensitive; up to 10 phrases.</small>
						</label>
						<label>
							<span>Forbidden response phrases</span>
							<textarea
								maxLength={1_010}
								onChange={(event) =>
									updateFormField(
										'forbiddenResponsePhrases',
										event.currentTarget.value,
									)
								}
								placeholder="One phrase per line"
								rows={3}
								value={form.forbiddenResponsePhrases}
							/>
							<small>Case-insensitive; up to 10 phrases.</small>
						</label>
					</div>
					<div className="ai-evaluation-form-actions">
						<button className="secondary-button" onClick={closeForm} type="button">
							Cancel
						</button>
						<button className="primary-button" disabled={busy}>
							{busy ? 'Saving…' : editingId ? 'Update case' : 'Create case'}
						</button>
					</div>
				</form>
			)}

			{loading ? (
				<div className="empty-configuration">Loading regression cases…</div>
			) : !agents.length ? (
				<div className="empty-configuration">
					Create and activate an AI team member before adding regression cases.
				</div>
			) : !cases.length ? (
				<div className="empty-configuration">
					Add a reusable case to establish the expected AI decision and response evidence.
				</div>
			) : (
				<div className="table-card ai-evaluation-table">
					<div className="ai-evaluation-row ai-evaluation-head standard-table-head">
						<span>Case</span>
						<span>Context</span>
						<span>Expected</span>
						<span>Draft</span>
						<span>Published</span>
						<span>Actions</span>
					</div>
					{cases.map((evaluationCase) => (
						<div className="ai-evaluation-case" key={evaluationCase.id}>
							<div className="table-row ai-evaluation-row">
								<div className="ai-evaluation-case-copy">
									<strong>{evaluationCase.name}</strong>
									<span>{evaluationCase.visitorMessage}</span>
								</div>
								<div>
									<strong>{evaluationCase.agentName}</strong>
									<span>{evaluationCase.departmentName ?? 'No department'}</span>
								</div>
								<div>
									<strong>{outcomeLabel(evaluationCase.expectedOutcome)}</strong>
									<span>
										{evaluationCase.minimumRelevanceScore === undefined
											? 'No evidence threshold'
											: `Evidence ≥ ${evaluationCase.minimumRelevanceScore.toFixed(2)}`}
									</span>
								</div>
								<span
									className={`ai-evaluation-status ${evaluationCase.latestComparison ? (evaluationCase.latestComparison.draft.passed ? 'pass' : 'fail') : 'pending'}`}
								>
									{resultLabel(evaluationCase.latestComparison?.draft)}
								</span>
								<span
									className={`ai-evaluation-status ${evaluationCase.latestComparison ? (evaluationCase.latestComparison.published.passed ? 'pass' : 'fail') : 'pending'}`}
								>
									{resultLabel(evaluationCase.latestComparison?.published)}
								</span>
								<div className="ai-evaluation-actions">
									<button
										className="secondary-button compact-button"
										disabled={busy || Boolean(runningId) || disabled}
										onClick={() => void run(evaluationCase)}
										type="button"
									>
										{runningId === evaluationCase.id ? 'Running…' : 'Run'}
									</button>
									<button
										className="secondary-button compact-button"
										disabled={busy || Boolean(runningId)}
										onClick={() => edit(evaluationCase)}
										type="button"
									>
										Edit
									</button>
									<button
										className="icon-button danger"
										disabled={busy || Boolean(runningId)}
										onClick={() => void remove(evaluationCase)}
										title="Delete regression case"
										type="button"
									>
										<TrashIcon />
										<span className="sr-only">Delete regression case</span>
									</button>
								</div>
							</div>
							{evaluationCase.latestComparison && (
								<details className="ai-evaluation-evidence">
									<summary>
										{comparisonLabel(
											evaluationCase.latestComparison.comparison,
										)}{' '}
										· View pass/fail evidence
									</summary>
									<div className="ai-evaluation-results">
										<EvaluationEvidence
											execution={evaluationCase.latestComparison.draft}
										/>
										<EvaluationEvidence
											execution={evaluationCase.latestComparison.published}
										/>
									</div>
									<small className="ai-evaluation-run-meta">
										{evaluationCase.latestComparison.evaluatedBy} ·{' '}
										{formatDateTime(
											evaluationCase.latestComparison.evaluatedAt,
										)}
									</small>
								</details>
							)}
						</div>
					))}
				</div>
			)}
		</section>
	);
}
